import { ArrayUtil } from "@coten-inc/utils";
import { z } from "zod";

import { displayCategoryName, displayName } from "~/common/functions";
import { GDTF } from "~/components/timeline";
import type { GetMapRecordGeoItemsQuery, GetRecordsForMapQuery } from "~/features/entities/gqls";
import { geoItemPlaceName } from "~/features/recordGeoItems/utils/geoItemLabel";
import type { Locale } from "~/i18n/resolveLocale";
import type { CoordsType } from "~/lib/map/types";

// Ported from V1 `~/models/entities/components/views/mapHelpers` (pure logic).

// Local copy so this feature depends on no other feature's geo utils.
const zPointCoords = z.object({ type: z.literal("Point"), coordinates: z.tuple([z.number(), z.number()]) });
const MAX_MAP_GEO_ITEMS_PER_RECORD = 100;
const parseCoords = (coords: unknown): CoordsType => {
  const parsed = zPointCoords.parse(coords);
  return { lat: parsed.coordinates[1], lng: parsed.coordinates[0] };
};

type MapPoint = {
  recordId: string;
  recordName: string;
  geoItemId: string;
  coords: CoordsType;
  label: string;
  categories: string[];
  categoryLabels: string[];
  startYear: number | null;
  endYear: number | null;
};

type MapPointProperties = {
  recordId: string;
  recordName: string;
  geoItemId: string;
  label: string;
  categories: string[];
  categoryLabels: string[];
  startYear: number | null;
  endYear: number | null;
};

export type MapPointGeoJSON = GeoJSON.Feature<GeoJSON.Point, MapPointProperties>;

export type HoverInfo = {
  x: number;
  y: number;
  object?: MapPointGeoJSON;
} | null;

export type YearBounds = { min: number; max: number } | null;

type MapGeoResponse = GetMapRecordGeoItemsQuery["cnutGetRecordGeoItemsByRecordIds"];
type MapGeoRecord = Extract<MapGeoResponse, { __typename?: "RecordGeoItemsByRecordIdsOutput" }>["records"][number];

export const requireMapGeoRecords = (response: MapGeoResponse): MapGeoRecord[] => {
  if (response.__typename !== "RecordGeoItemsByRecordIdsOutput") {
    throw new Error(response.message);
  }
  return response.records;
};

const tryParseCoords = (coords: unknown): CoordsType | null => {
  try {
    return parseCoords(coords);
  } catch {
    return null;
  }
};

const safeParseYear = (gdtfStr: string | null | undefined, isStart: boolean): number | null => {
  if (!gdtfStr) return null;
  try {
    const g = new GDTF(gdtfStr, isStart);
    return g.isValid() ? g.year : null;
  } catch {
    return null;
  }
};

export const recordsToMapPoints = (
  records: GetRecordsForMapQuery["records"],
  geoRecords: MapGeoRecord[],
  locale: Locale,
): MapPoint[] => {
  const points: MapPoint[] = [];
  const geoRecordsById = new Map(geoRecords.map((record) => [record.id, record]));
  for (const record of records) {
    const categories = record.recordMetadata.recordMetadataCategories.map((rmc) => rmc.category.name);
    const categoryLabels = record.recordMetadata.recordMetadataCategories.map((rmc) =>
      displayCategoryName(
        { name: rmc.category.name, i18nLabel: rmc.category.i18nLabel as Record<string, unknown> | null },
        locale,
      ),
    );
    const startYear = safeParseYear(record.date, true);
    const endYear = safeParseYear(record.endDate, false);
    const recordName = displayName(record, locale);
    // 旧Hasura queryと同じ上限を維持し、異常に多いGeoItemでdeck.glの描画量が膨らむのを防ぐ。
    // The reader returns one row per link entry; a point is per GeoItem, and the cap counts GeoItems.
    const geoItems = ArrayUtil.uniqBy([...(geoRecordsById.get(record.id)?.recordGeoItems ?? [])], "geoItemId");
    for (const rgi of geoItems.slice(0, MAX_MAP_GEO_ITEMS_PER_RECORD)) {
      const coords = tryParseCoords(rgi.geoItem.coords);
      if (!coords) continue;
      // An unnamed GeoItem gets no text: the map shows its dot alone.
      const label = geoItemPlaceName(rgi.geoItem.i18nLabel, locale) ?? "";
      points.push({
        recordId: record.id,
        recordName,
        geoItemId: rgi.geoItemId,
        coords,
        label,
        categories,
        categoryLabels,
        startYear,
        endYear,
      });
    }
  }
  return points;
};

export const pointToGeoJSON = (pt: MapPoint): MapPointGeoJSON => ({
  type: "Feature",
  properties: {
    recordId: pt.recordId,
    recordName: pt.recordName,
    geoItemId: pt.geoItemId,
    label: pt.label,
    categories: pt.categories,
    categoryLabels: pt.categoryLabels,
    startYear: pt.startYear,
    endYear: pt.endYear,
  },
  geometry: {
    type: "Point",
    coordinates: [pt.coords.lng, pt.coords.lat],
  },
});

export const extractCharacterSet = (features: MapPointGeoJSON[]): string[] =>
  Array.from(new Set(features.flatMap((f) => Array.from(f.properties.label))));

export const isInYearRange = (point: MapPoint, range: [number, number] | null): boolean => {
  if (!range) return true;
  const [rangeStart, rangeEnd] = range;
  const entityStart = point.startYear;
  const entityEnd = point.endYear ?? point.startYear;
  if (entityStart === null) return true;
  if (entityEnd !== null && entityEnd < rangeStart) return false;
  if (entityStart > rangeEnd) return false;
  return true;
};
